import React from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { setWatermarks } from '../../actions';
import previewImage from '../../../../../assets/images/preview.jpg';

import style from './style.module.scss';

const CORNER_OPTIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const WatermarksEditor = ({
  watermarks = [],
  setWatermarksAction,
}) => {
  const remove = (i) => {
    const updated = watermarks.filter((_, j) => j !== i);
    setWatermarksAction(updated);
  };

  const updateAt = (i, updates) => {
    const updated = [...watermarks];
    updated[i] = { ...updated[i], ...updates };
    setWatermarksAction(updated);
  };

  const clampNum = (v, min, max) => {
    const n = Number.parseInt(v, 10);
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
  };

  const getWatermarkStyle = (wm) => {
    const corner = wm.corner || 'top-left';
    const wmWidth = wm.wmWidthPct ?? 16;
    const padX = wm.padXPct ?? 2;
    const padY = wm.padYPct ?? 2;
    const base = {
      width: `${wmWidth}%`,
      objectFit: 'contain',
      position: 'absolute',
      zIndex: 1,
    };
    if (corner === 'top-left') return { ...base, left: `${padX}%`, top: `${padY}%` };
    if (corner === 'top-right') return { ...base, right: `${padX}%`, top: `${padY}%` };
    if (corner === 'bottom-left') return { ...base, left: `${padX}%`, bottom: `${padY}%` };
    if (corner === 'bottom-right') return { ...base, right: `${padX}%`, bottom: `${padY}%` };
    return { ...base, left: `${padX}%`, top: `${padY}%` };
  };

  return (
    <div className={classnames('mb-3', style.watermarksLayout)}>
      <div className={style.editorColumn}>
        {watermarks.length > 0 && (
          <ul className={style.watermarkList}>
            {watermarks.map((wm, i) => (
              <li key={wm.watermarkKey || i} className={style.watermarkItem}>
                <div className={style.thumbnailWrap}>
                  <img
                    src={wm.watermarkKey}
                    alt=""
                    className={style.thumbnail}
                  />
                  <a
                    className={style.downloadBtn}
                    href={wm.watermarkRawUrl || wm.watermarkKey}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download"
                    aria-label="Download"
                  >
                    <FontAwesomeIcon icon={faDownload} className="align-middle" />
                  </a>
                  <button
                    type="button"
                    className={style.removeBtn}
                    onClick={() => remove(i)}
                    title="Delete"
                    aria-label="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} className="align-middle" />
                  </button>
                </div>
                <div className={style.configControls}>
                  <Form.Group className={style.configField}>
                    <Form.Label>Corner</Form.Label>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={wm.corner || 'top-left'}
                      onChange={(e) => updateAt(i, { corner: e.target.value })}
                    >
                      {CORNER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group className={style.configField}>
                    <Form.Label>Width %</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={100}
                      value={wm.wmWidthPct ?? 16}
                      onChange={(e) => updateAt(
                        i,
                        { wmWidthPct: clampNum(e.target.value, 0, 100) },
                      )}
                    />
                  </Form.Group>
                  <Form.Group className={style.configField}>
                    <Form.Label>Pad X %</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={100}
                      value={wm.padXPct ?? 2}
                      onChange={(e) => updateAt(i, { padXPct: clampNum(e.target.value, 0, 100) })}
                    />
                  </Form.Group>
                  <Form.Group className={style.configField}>
                    <Form.Label>Pad Y %</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={100}
                      value={wm.padYPct ?? 2}
                      onChange={(e) => updateAt(i, { padYPct: clampNum(e.target.value, 0, 100) })}
                    />
                  </Form.Group>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={style.previewColumn}>
        <div className={style.previewWrap}>
          <span className={style.previewLabel}>Preview</span>
          <div className={style.previewContainer}>
            <img src={previewImage} className={style.previewBg} alt="Preview" />
            {watermarks.map((wm, i) => (
              <img
                key={wm.watermarkKey || i}
                src={wm.watermarkKey}
                alt=""
                className={style.previewWatermark}
                style={getWatermarkStyle(wm)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state) => {
    const sections = state.graphics?.sections || [];
    const watermarksSection = sections.find((s) => s.key === 'watermarks');
    const watermarks = watermarksSection?.data?.watermarks || [];
    return { watermarks };
  },
  { setWatermarksAction: setWatermarks },
)(WatermarksEditor);
